/**
 * Does RMF still hold this task, and is its robot in a lift car?
 *
 * WHY THE CONSOLE HAS TO ASK (sg-nomad-01, 2026-09-15). Worker 0.4.28
 * projected RMF's `error` -- which means "needs attention, still running",
 * and which the fleet adapter re-enters every 5 s while it retries a plan --
 * as the terminal `failed`. `TaskControlActions` offers Cancel and Kill on
 * non-terminal states only, so for 47 minutes the ONE lever that would have
 * ended the wedge was invisible while the robot sat at a disarmed lift.
 *
 * Worker 0.4.29 fixes the mapping, but the console must still answer the
 * question for the records a previous worker wrote and for anything else that
 * ends up terminal-on-screen while a robot is running it. So the controls
 * follow OWNERSHIP as well as status.
 *
 * AND OWNERSHIP IS NOT ENOUGH ON ITS OWN. A task cancelled while its robot is
 * inside a car leaves the robot parked there holding the lift, and the only
 * safe way out is the push-out procedure. So the second question -- is the
 * robot in or boarding a car? -- decides whether the buttons appear at all.
 *
 * These are PURE FUNCTIONS with no hooks and no fetching, so the rule can be
 * tested directly rather than through a rendered tree, and so the same rule
 * can be used by the drawer, the robot panel and anything added later without
 * three spellings of it.
 */
import type { LiftViewModel } from '../../api/adapters';
import type { RobotStateView, TaskStateView } from '../../api/rmf-client';

/**
 * `rmf_lift_msgs/LiftRequest.REQUEST_END_SESSION`. Defined ONCE, here, and
 * checked against the worker's `_LIFT_REQUEST_END_SESSION` -- a standing
 * request of this type is the supervisor LETTING GO of the car, not a robot
 * boarding it, and reading it as a boarding would hide Cancel for a robot
 * standing in the hall.
 */
export const END_SESSION_REQUEST_TYPE = 0;

/**
 * THE ONE RELEASE CLEARANCE, 0.225 m. The same number the worker, the
 * api-server, the fleet adapter (`_LIFT_CLEAR_MARGIN_M`), W22 and A8 use --
 * set by the user against the review's recommended 0.70 m, with the stated
 * accepted risk that up to 0.225 m of robot body can overhang the sill.
 */
export const LIFT_CLEAR_MARGIN_M = 0.225;

/**
 * How fresh a robot state must be to say it is running a task. The robot
 * publishes at 1 Hz, so 30 s is thirty missed samples: well past any jitter
 * and well inside "the adapter is gone".
 */
export const ROBOT_STATE_MAX_AGE_MS = 30_000;

/**
 * Is this robot executing this task RIGHT NOW?
 *
 * Both halves are required. The task id alone would believe a robot state
 * from ten minutes ago -- which is exactly the state an orphaned task's robot
 * has -- and offering an End button on that is offering to command a robot
 * nobody has heard from.
 */
export function robotHoldsTask(
  task: Pick<TaskStateView, 'id'>,
  robot: Pick<RobotStateView, 'taskId' | 'lastSeen'> | undefined,
  nowMs: number,
  maxAgeMs: number = ROBOT_STATE_MAX_AGE_MS,
): boolean {
  if (!robot || robot.taskId !== task.id) return false;
  if (!robot.lastSeen) return false;
  const seen = Date.parse(robot.lastSeen);
  if (!Number.isFinite(seen)) return false;
  return nowMs - seen <= maxAgeMs;
}

/**
 * Distance from a robot centre to one cabin rectangle, or `undefined` when
 * the geometry cannot answer.
 *
 * The rectangle is `width` along the cabin's own x axis and `depth` along its
 * y, about `ref`, rotated by `refYaw` -- the same rectangle the worker, the
 * api-server and the fleet adapter measure against. IGNORING `refYaw` puts
 * the long side the wrong way round; at sg-nomad-01 the two sides differ by
 * 0.15 m against a 0.225 m margin, so that error can call a robot on the
 * landing "inside" and hide the Cancel button an operator needs.
 *
 * `undefined` means CANNOT SAY -- a lift with no registry geometry, or a
 * robot on a level this lift does not serve -- and never "clear".
 */
export function cabinClearanceM(
  lift: Pick<LiftViewModel, 'levels' | 'ref' | 'refYaw' | 'width' | 'depth'>,
  location: { map: string; x: number; y: number },
): number | undefined {
  const { ref, width, depth } = lift;
  if (!ref || !width || !depth) return undefined;
  if (lift.levels?.length && location.map && !lift.levels.includes(location.map)) {
    return undefined;
  }
  const yaw = lift.refYaw ?? 0;
  const dx = location.x - ref.x;
  const dy = location.y - ref.y;
  const cos = Math.cos(-yaw);
  const sin = Math.sin(-yaw);
  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;
  const overX = Math.abs(localX) - width / 2;
  const overY = Math.abs(localY) - depth / 2;
  if (overX <= 0 && overY <= 0) return 0;
  return Math.hypot(Math.max(0, overX), Math.max(0, overY));
}

/**
 * The lift this robot is in or boarding, or `undefined`.
 *
 * Three bases, checked in this order and for this reason:
 *
 *   cabin pose        -- the robot's own position. FIRST because it is the
 *                        only one that sees a robot which entered a car in
 *                        the last second: the session and the standing
 *                        request are 1 Hz caches and lag it (challenge S5).
 *   rmfSessionId      -- the LIFT echoes this robot's session id.
 *   standingRequest   -- the supervisor holds a non-END request under this
 *                        robot's id, i.e. a boarding in progress.
 */
export function heldLift(
  robot: Pick<RobotStateView, 'fleet' | 'name' | 'location'> | undefined,
  lifts: readonly LiftViewModel[] | undefined,
): LiftViewModel | undefined {
  if (!robot || !lifts?.length) return undefined;
  const sessionId = `${robot.fleet}/${robot.name}`;

  if (robot.location) {
    for (const lift of lifts) {
      const clearance = cabinClearanceM(lift, robot.location);
      if (clearance !== undefined && clearance <= LIFT_CLEAR_MARGIN_M) return lift;
    }
  }
  for (const lift of lifts) {
    if (lift.rmfSessionId && lift.rmfSessionId === sessionId) return lift;
  }
  for (const lift of lifts) {
    const standing = lift.standingRequest;
    if (
      standing &&
      standing.session_id === sessionId &&
      standing.request_type !== END_SESSION_REQUEST_TYPE
    ) {
      return lift;
    }
  }
  return undefined;
}

/** The three v1 states a task never leaves. */
export function isTerminalTaskStatus(status: TaskStateView['status']): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}
